import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  InputLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  IconButton,
  NativeSelect,
  FormControl,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useForm, FormProvider } from 'react-hook-form';
// import { enqueueSnackbar } from 'notistack';
import { getParameter } from './../../utils/Utils';
// import ReactQuill from 'react-quill-new';
// import 'react-quill-new/dist/quill.snow.css';

// === 각 탭 콘텐츠 ===
import PatientInfoForm from './PatientInfoForm'; // 우리가 만든 환자 정보 폼
import AdverseEventForm from './AdverseEventForm';
import DrugInfoForm from './DrugInfoForm';
import ReportInfoForm from './ReportInfoForm';
import FinalOpinionForm from './FinalOpinionForm';

import { ORGANIZATION_SERVER, Herbaladr_SERVER } from '../../Config';
// import { replaceBrTag } from '../../../utils/util';
// import { useSnackbar } from 'notistack';
// import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
axios.defaults.withCredentials = true;

const CustomGrid2 = styled(Grid)({
  padding: '8px',
  marginBottom: '4px',
});

function TabPanel({ value, index, children }) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// 가독성 강화 테마 (라이트/다크 공통 토큰 + 다크 모드 기본)
const theme = createTheme({
  palette: {
    mode: 'light', // 필요 시 'light'로 변경[5]
    primary: { main: '#7CB8FF' }, // 탭 강조색 (임의 예시)[5][9]
    secondary: { main: '#FFB74D' },
    contrastThreshold: 4.5, // WCAG 대비 기준 강화[9]
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48, // 탭 바 높이 상향[8]
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        },
        indicator: {
          height: 4, // 인디케이터 두껍게[1][11]
          borderRadius: 2,
        },
      },
      defaultProps: {
        textColor: 'inherit',
        variant: 'scrollable',
        scrollButtons: 'auto',
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48, // 각 탭 높이[8]
          padding: '10px 16px',
          fontWeight: 600, // 굵게
          fontSize: '0.95rem', // 약간 크게

          color: 'black',
          '&.Mui-selected': {
            color: 'black',
          },
        },
      },
    },
  },
});

const CustomFormControl = styled(FormControl)({
  '& .MuiInputLabel-root': {
    color: theme.main,
  },
  '& label.Mui-focused': {
    color: theme.orange,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.orange,
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.orange,
    },
  },
});

const HerbalAdrAddPage = (props) => {
  // const quillRef = useRef(null);
  const matches = useMediaQuery('(max-width:768px)');
  const { state } = useLocation();
  const [organizationList, setOrganizationList] = useState([]);
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.userData);
  const [tabValue, setTabValue] = useState(0);

  // const handleChange = (_event, newValue) => {
  //   setTabValue(newValue);
  // };

  // react-hook-form 전역 폼 인스턴스
  const methods = useForm({
    defaultValues: {
      groupId: '',
      patientInitial: '',
      gender: '',
      // 생년월일: 단일 8자리(YYYYMMDD)
      birthDate: '',
      age: '',
      // 체크박스 → 라디오로 변경: 단일 선택 문자열
      ageGroup: '', // "", "fetus", "d0_28", "d28_m24", "m24_y12", "y12_y19", "y19_y65", "y65_plus"
      weight: '',
      height: '',
      // 체크박스 → 라디오로 변경: 단일 선택 문자열
      race: '', // "", "korean", "asianNonKorean", "white", "black"
      deathDate: '',
      patientId: '',

      gestationPeriodValue: '',
      gestationPeriodUnit: '',
      birthWeight: '',
      birthWeeks: '',
      miscarriageDate: '',

      // 나. 이상사례 (배열 구조)
      adverseEvents: [
        {
          gastrointestinalSymptoms: {
            nausea: false,
            diarrhea: false,
            vomiting: false,
            abdominalPain: false,
            abdominalDistension: false,
            constipation: false,
            abdominalDiscomfort: false,
            indigestion: false,
            gastrointestinalDisorder: false,
          },
          skinSymptoms: { jaundice: false, rash: false, itching: false, hives: false },
          neuropsychiatricSymptoms: {
            headache: false,
            dizziness: false,
            drowsiness: false,
            anxiety: false,
            insomnia: false,
            tremor: false,
          },
          systemicSymptoms: {
            lossOfAppetite: false,
            fatigueMalaiseLethargy: false,
            weightLoss: false,
            edema: false,
            sweating: false,
            abnormalLiverFunctionTest: false,
            liverDysfunction: false,
            abnormalBloodTest: false,
            fever: false,
            hypertension: false,
            tachycardia: false,
            bradycardia: false,
            xerostomia: false,
          },
          entSymptoms: { xerostomia: false },
          genitourinarySymptoms: { darkColoredUrine: false, dysuria: false, vaginalBleeding: false },
          respiratorySymptoms: { dyspnea: false, cough: false },
          cardiovascularSymptoms: { palpitations: false, chestTightness: false, chestPain: false, arrhythmia: false },
          musculoskeletalSymptoms: { arthralgia: false },
          other: '',
          adverseEventStartDate: '',
          adverseEventEndDate: '',
          adverseEventCountry: '',
          adverseEventDuration: '',
          adverseEventDurationUnit: '',
          adverseEventSeverityCriteria: {
            resultsInDeath: false,
            lifeThreatening: false,
            requiresHospitalization: false,
            causesDisability: false,
            congenitalAnomaly: false,
            medicallyImportantEvent: false,
            notApplicable: false,
          },
          adverseEventHospitalization: '',
          adverseEventOutcome: '',
          diagnosticTestInfo: [],
        },
      ],
      // 다. 의약품
      drugInfos: [
        {
          herbalPrescription: {
            drugName: '',
            totalDoseCount: '',
            totalPackCount: '',
            packsPerDay: '',
            volumePerPack: '',
            decoctionLocation: '', // "inHospital" | "external"
            herbCompositions: [
              { herbName: '', amountGram: '' }, // 기본 1개
            ],
            herbalItemInfos: [
              { itemHerbName: '', manufacturer: '', origin: '' }, // 기본 1개
            ],
          },
          drugAdministration: [
            {
              indication: '',
              startDate: '',
              endDate: '',
              totalPacksUntilOnset: '',
              route: '',
              routeOther: '',
              dosageIntervalUnit: '',
              dosageIntervalCount: '',
              dosagePeriod: '',
              dosageForm: '',
              dosageFormOther: '',
            },
          ],
          drugAdministrationFetus: {
            routeParent: '',
            firstExposureGestation: {
              value: '',
              unit: '', // "day" | "week" | "month" | "trimester"
            },
          },
          adverseEventTreatment: '',
          concomitantTherapy: '',
          concomitantDrugs: [
            {
              concomitantDrugName: '',
              dailyTotalDosage: '',
              concomitantDrugUnit: '',
              administrationRoute: '',
              administrationStartDate: '',
              administrationEndDate: '',
              isOngoing: '',
            },
          ],
          pastHistory: {
            details: '',
            startDate: '',
            isOngoing: '',
            endDate: '',
            note: '',
            familyHistory: false,
            familyHistoryDetail: '',
            previousMedications: {
              name: '',
              startDate: '',
              endDate: '',
              indication: '',
              adverseReactionNote: '',
            },
          },
          adverseEventRelevance: [
            {
              symptomName: '',
              rechallengeOutcome: '',
              causalAssessmentSets: [
                {
                  whoUmc: { type: 'who-umc', result: '' },
                  other: { type: 'other', methodOther: '', resultOther: '' },
                },
              ],
            },
          ],
        },
      ],
      // 라. 보고 정보
      reporter: {
        type: '',
        name: '',
        phone: '',
        email: '',
      },
      reportDate: '',
      dateFirstRecognized: '',
      dateOfMostRecentAdverseEvent: '',
      reportType: '',
      // 마. 종합 의견
      caseDescription: '',
      reporterOpinion: '',
      originalReporterOpinion: '',
    },
    // resolver: yupResolver(validationSchema),
  });

  // const onSubmit = (data) => {
  //   console.log('전체 폼 데이터', data);
  // };

  // 탭 구성
  const tabs = [
    { label: '가. 환자 정보', content: <PatientInfoForm /> },
    { label: '나. 이상사례∙약물이상반응 정보', content: <AdverseEventForm /> },
    { label: '다. 의약품등 정보', content: <DrugInfoForm /> },
    { label: '라. 보고 정보', content: <ReportInfoForm /> },
    { label: '마. 종합의견', content: <FinalOpinionForm /> },
  ];

  const { register, reset } = methods;

  // const onSubmit = async (data, e) => {
  //   let axiosConfig = {
  //     headers: {
  //       'Content-Type': 'multipart/form-data',
  //     },
  //   };

  //   let formData = new FormData();
  //   formData.append('publisherId', user._id);
  //   formData.append('publisherName', user.name);
  //   formData.append('collectionPeriod', JSON.stringify(data.collectionPeriod));
  //   formData.append('dataTarget', data.dataTarget);
  //   formData.append('dataType', data.dataType);
  //   formData.append('sampleCount', data.sampleCount);
  //   formData.append('researchDirector', data.researchDirector);
  //   formData.append('collectionMethod', data.collectionMethod);
  //   formData.append('groupId', user.role === 'user' ? groupList.find((v) => v._id === state.group)._id : data.groupId);
  //   formData.append('groupName', groupList.find((v) => v._id === state.group).name);
  //   formData.append('status', true);

  //   // 첨부파일 formdata에 추가
  //   if (files) {
  //     for (let i = 0; i < files.length; i++) {
  //       formData.append('files', files[i]);
  //     }
  //   }

  //   await axios.post(`${process.env.REACT_APP_SERVER}${POST_SERVER}`, formData, axiosConfig).then((res) => {
  //     if (res.data === 'ok') {
  //       enqueueSnackbar('New Post Add Completed', {
  //         variant: 'success',
  //         autoHideDuration: 2500,
  //       });
  //       navigate(-1);
  //     }
  //   });
  // };

  useEffect(() => {
    if (user.role === 'user') {
    }
    axios.get(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/list`).then((res) => {
      // console.log(res.data);
      setOrganizationList(res.data);
    });
    methods.setValue('groupId', getParameter('groupId'));
    // methods.setValue('_id', getParameter('objId'));

    if (state ? state.objId : getParameter('objId')) {
      axios
        .get(`${process.env.REACT_APP_SERVER}${Herbaladr_SERVER}/${state ? state.objId : getParameter('objId')}`)
        .then((res) => {
          reset(res.data, { keepDirtyValues: true }); // 서버값으로 초기화하되 사용자가 수정한 값은 유지할 수 있음

          // reset(res.data, { keepValues: false, keepDefaultValues: false, keepDirty: false });
        });
    }
  }, []);

  return (
    <div className="ProjectPage">
      <FormProvider {...methods}>
        <form>
          <ThemeProvider theme={theme}>
            <Container
              maxWidth="lg"
              sx={
                matches
                  ? { paddingTop: '116px', paddingBottom: '100px' }
                  : { paddingTop: '168px', paddingBottom: '100px' }
              }
            >
              <Box>
                <Grid container direction="row">
                  <Grid size={12} container alignItems="flex-end" justifyContent="flex-end" sx={{ pr: 3 }}>
                    <CustomFormControl variant="standard" size="small" sx={{ width: '60%' }}>
                      <InputLabel shrink htmlFor="organization">
                        Organization
                      </InputLabel>

                      <NativeSelect
                        id="organization"
                        label="Type"
                        {...register('groupId')}
                        value={methods.watch('groupId') || 'None'}
                        disabled={user.role !== 'super admin'}
                        fullWidth
                      >
                        <option value={'None'}>Select a Organization...</option>
                        {organizationList &&
                          organizationList.map((v, i) => (
                            <option key={v._id} value={v._id}>
                              {`[${v.name}] - `}
                              {v.projectName !== undefined ? v.projectName : ''}
                            </option>
                          ))}
                      </NativeSelect>
                    </CustomFormControl>
                  </Grid>

                  <Grid size={{ xs: 12 }} sx={{ pr: 3, pl: 3 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid container alignItems="center" wrap="nowrap" columnGap={1}>
                    <Grid>
                      <IconButton
                        aria-label="뒤로가기"
                        edge="start"
                        onClick={() => navigate(`/HerbalAdr?groupId=${getParameter('groupId')}`)}
                        size="small"
                      >
                        <ArrowBackIcon />
                      </IconButton>
                    </Grid>
                    <Grid>
                      <Typography variant="h4" align="left" sx={{ fontWeight: 900 }} noWrap>
                        첩약 이상반응 보고
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container size={12}>
                    <CustomGrid2 size={{ xs: 12 }}>
                      <CssBaseline />
                      <Box sx={{ width: '100%' }}>
                        {/* 탭 헤더 */}
                        <Tabs
                          value={tabValue}
                          onChange={(e, nv) => setTabValue(nv)}
                          variant="scrollable"
                          scrollButtons="auto"
                        >
                          {tabs.map((tab, idx) => (
                            <Tab key={idx} label={tab.label} />
                          ))}
                        </Tabs>

                        {/* 탭 내용 */}
                        {tabs.map((tab, idx) => (
                          <TabPanel key={idx} value={tabValue} index={idx}>
                            {React.isValidElement(tab.content)
                              ? React.cloneElement(tab.content, {
                                  tabIndex: idx,
                                })
                              : tab.content}
                          </TabPanel>
                        ))}
                      </Box>
                    </CustomGrid2>
                  </Grid>

                  {/* <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }}
              size={12}
            >
              <Grid size={'grow'}></Grid>
              <CustomGrid2 size={2}>
                <Button
                  title="Click me"
                  variant="outlined"
                  onClick={() => {
                    navigate(-1);
                  }}
                  sx={{ marginRight: '16px', textTransform: 'none' }}
                >
                  Close
                </Button>
                <Button variant="contained" onClick={handleSubmit(onSubmit)} sx={{ textTransform: 'none' }}>
                  Save
                </Button>
              </CustomGrid2>
            </Grid> */}
                </Grid>
              </Box>
            </Container>
          </ThemeProvider>
        </form>
      </FormProvider>
    </div>
  );
};

export default HerbalAdrAddPage;
